/**
 * Home visit patient search form (訪問予定表 患者毎 — filter panel).
 * Uses CollapsibleCard for section wrapper and grid layout for form fields.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, AppSelect, AppRadio, AppRadioGroup } from '@/components/atoms';
import AppTextField from '@/components/atoms/AppTextField';
import { Search } from '@/components/atoms/AppIcon';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { RA2NDatePicker } from '@/components/organisms';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { KANA_INITIAL_OPTIONS } from '@/constants/visit';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import useAppToast from '@/hooks/useAppToast';
import dayjs from 'dayjs';
import type { HomeVisitPatientMassager } from '../types';

export interface SearchFormValues {
  visitDateFrom: string;
  visitDateTo: string;
  massagerRadio: '1' | '2';
  massagerId: number | null;
  searchRadio: '1' | '2';
  serialNumber: string;
  patientName: string;
  kanaInitial: number | null;
  sortRadio: '1' | '2' | '3';
}

interface Props {
  onSearch: (values: SearchFormValues) => void;
  onVisitDateRangeChange?: (range: {
    visit_datetime_from: string | null;
    visit_datetime_to: string | null;
  }) => void;
  massagers: HomeVisitPatientMassager[];
  serviceType: string;
  isLoading: boolean;
  totalCount: number;
}

const HomeVisitPatientSearchForm: React.FC<Props> = ({
  onSearch,
  onVisitDateRangeChange,
  massagers,
  serviceType,
  isLoading,
  totalCount,
}) => {
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const firstOfMonth = useMemo(() => convGtJDate(dayjs().startOf('month')), []);

  const [form, setForm] = useState<SearchFormValues>({
    visitDateFrom: firstOfMonth,
    visitDateTo: firstOfMonth,
    massagerRadio: '1',
    massagerId: null,
    searchRadio: '1',
    serialNumber: '',
    patientName: '',
    kanaInitial: null,
    sortRadio: '1',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useAppToast();

  const notifyVisitDateRangeChange = useCallback(
    (visitDateFrom: string, visitDateTo: string) => {
      onVisitDateRangeChange?.({
        visit_datetime_from: dateValueToIso(visitDateFrom),
        visit_datetime_to: dateValueToIso(visitDateTo),
      });
    },
    [onVisitDateRangeChange]
  );

  const updateField = useCallback(
    <K extends keyof SearchFormValues>(key: K, value: SearchFormValues[K]) => {
      setForm(prev => ({ ...prev, [key]: value }));
      setErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    []
  );

  const massagerOptions = useMemo(
    () =>
      massagers.map(m => ({
        value: m.id,
        label: `${m.serial_number ?? ''}:${m.name}`,
      })),
    [massagers]
  );
  const massagerListEmpty = massagers.length === 0;

  const handleSearch = useCallback(() => {
    const errs = (() => {
      const e: Record<string, string> = {};
      if (!form.visitDateFrom || !dateValueToIso(form.visitDateFrom)) {
        e.visitDateFrom = '【訪問年月日開始日】を入力してください';
      }
      if (!form.visitDateTo || !dateValueToIso(form.visitDateTo)) {
        e.visitDateTo = '【訪問年月日終了日】を入力してください';
      }
      if (form.massagerRadio === '2' && !form.massagerId) {
        e.massagerId = isA2 ? 'はり・きゅう師を選択してください' : 'マッサージ師を選択してください';
      }
      return e;
    })();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs)[0]);
      return;
    }
    onSearch(form);
  }, [form, isA2, onSearch, toast]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <CollapsibleCard title="予定表(患者毎)検索">
      <div onKeyDown={handleKeyDown}>
        <div className="grid grid-cols-[120px_1fr] items-start gap-x-4 gap-y-3">
          {/* Row 1: Date range */}
          <label className="pt-1 text-sm">訪問年月日</label>
          <div className="flex items-start gap-2">
            <div>
              <RA2NDatePicker
                value={form.visitDateFrom}
                onChange={val => {
                  setForm(prev => {
                    notifyVisitDateRangeChange(val, prev.visitDateTo);
                    return { ...prev, visitDateFrom: val, massagerId: null };
                  });
                  setErrors(prev => {
                    const next = { ...prev };
                    delete next.visitDateFrom;
                    delete next.visitDateTo;
                    delete next.massagerId;
                    return next;
                  });
                }}
                error={!!errors.visitDateFrom}
              />
              {errors.visitDateFrom && (
                <div className="text-xs text-red-500">{errors.visitDateFrom}</div>
              )}
            </div>
            <span className="pt-1 text-sm">～</span>
            <div>
              <RA2NDatePicker
                value={form.visitDateTo}
                onChange={val => {
                  setForm(prev => {
                    notifyVisitDateRangeChange(prev.visitDateFrom, val);
                    return { ...prev, visitDateTo: val, massagerId: null };
                  });
                  setErrors(prev => {
                    const next = { ...prev };
                    delete next.visitDateFrom;
                    delete next.visitDateTo;
                    delete next.massagerId;
                    return next;
                  });
                }}
                error={!!errors.visitDateTo}
              />
              {errors.visitDateTo && (
                <div className="text-xs text-red-500">{errors.visitDateTo}</div>
              )}
            </div>
          </div>

          {/* Row 2: Massager filter */}
          <label className="pt-1 text-sm">{massagerLabel}</label>
          <div className="flex items-center gap-2">
            <AppRadioGroup
              value={form.massagerRadio}
              onChange={e => {
                const val = e.target.value as '1' | '2';
                updateField('massagerRadio', val);
                if (val === '1') updateField('massagerId', null);
              }}
            >
              <AppRadio value="1">指定なし</AppRadio>
              <AppRadio value="2">指定あり</AppRadio>
            </AppRadioGroup>
            <div>
              <AppSelect
                className="w-60"
                value={form.massagerId ?? undefined}
                onChange={val =>
                  updateField('massagerId', val != null && val !== '' ? Number(val) : null)
                }
                options={massagerListEmpty ? [] : massagerOptions}
                notFoundContent={massagerListEmpty ? 'データがありません' : undefined}
                disabled={form.massagerRadio === '1'}
                placeholder="選択してください"
              />
              {errors.massagerId && <div className="text-xs text-red-500">{errors.massagerId}</div>}
            </div>
          </div>

          {/* Row 3: Search type */}
          <label className="pt-1 text-sm">検索条件</label>
          <div className="flex items-center gap-2">
            <AppRadioGroup
              value={form.searchRadio}
              onChange={e => {
                const val = e.target.value as '1' | '2';
                updateField('searchRadio', val);
                if (val === '1') {
                  updateField('patientName', '');
                  updateField('kanaInitial', null);
                } else {
                  updateField('serialNumber', '');
                }
              }}
            >
              <AppRadio value="1">受診者番号</AppRadio>
              <AppTextField
                className="w-32 mr-2"
                value={form.serialNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('serialNumber', e.target.value)
                }
                maxLength={6}
                disabled={form.searchRadio !== '1'}
                placeholder="受診者番号"
              />
              <AppRadio value="2">受診者名</AppRadio>
              <AppTextField
                className="w-40"
                value={form.patientName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField('patientName', e.target.value)
                }
                maxLength={255}
                disabled={form.searchRadio !== '2'}
                placeholder="受診者名"
              />
            </AppRadioGroup>
            <span className="text-sm">仮名頭文字</span>
            <AppSelect
              className="w-28"
              value={form.kanaInitial ?? undefined}
              onChange={val => updateField('kanaInitial', val ? Number(val) : null)}
              options={KANA_INITIAL_OPTIONS}
              disabled={form.searchRadio !== '2'}
              placeholder="すべて"
            />
          </div>

          {/* Row 4: Sort */}
          <label className="pt-1 text-sm">並び順</label>
          <div className="flex items-center gap-2">
            <AppRadioGroup
              value={form.sortRadio}
              onChange={e => updateField('sortRadio', e.target.value as '1' | '2' | '3')}
            >
              <AppRadio value="1">受診者番号順</AppRadio>
              <AppRadio value="2">仮名順</AppRadio>
              <AppRadio value="3">{massagerLabel}順</AppRadio>
            </AppRadioGroup>
          </div>
        </div>

        {/* Search button + 該当件数 badge */}
        <div className="flex items-center mt-4">
          <div className="flex-1" />
          <AppButton type="primary" onClick={handleSearch} loading={isLoading}>
            <Search /> 検索
          </AppButton>
          <div className="flex justify-end flex-1">
            <span className="px-3 py-1 text-sm font-medium leading-tight text-center text-black rounded-md bg-amber-400">
              該当件数 {totalCount}
            </span>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default HomeVisitPatientSearchForm;
